// Pieces for "connected apps" that the person's page, the staff account page and the admin's
// Connections page all share: the domain line with its "verified" mark, a disconnect button
// with a confirm step, and the list of an account's own apps.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { scopeWords, shortDate } from "../lib/connect";
import type { Connection } from "../lib/types";

/** "verified" means the app identified itself with a published page, not just a typed-in name. */
export function VerifiedMark() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-soft px-2 py-0.5 text-[12px] font-semibold text-ink">
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="text-accent-text">
        <path d="M2.5 6.4 5 8.8 9.6 3.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Verified
    </span>
  );
}

/** The address an app comes from, with the mark when it's verified. */
export function AppHost({ host, verified }: { host: string; verified: boolean }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="break-all text-muted" data-testid="app-host">
        {host}
      </span>
      {verified ? <VerifiedMark /> : null}
    </span>
  );
}

/**
 * A button that asks "are you sure?" in place before doing something that can't be undone.
 * Focus goes to the safe choice when the question opens.
 */
export function InlineConfirm({
  label,
  question,
  confirmLabel,
  busyLabel,
  onConfirm,
  describedBy,
}: {
  label: string;
  question: string;
  confirmLabel: string;
  busyLabel: string;
  onConfirm: () => Promise<void>;
  /** An accessible name for the trigger that says which row it belongs to. */
  describedBy: string;
}) {
  const [step, setStep] = useState<"idle" | "asking" | "busy">("idle");
  const [failed, setFailed] = useState(false);
  const keep = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (step === "asking") keep.current?.focus();
  }, [step]);

  const confirm = async () => {
    setStep("busy");
    setFailed(false);
    try {
      await onConfirm();
    } catch {
      setFailed(true);
      setStep("asking");
    }
  };

  if (step === "idle") {
    return (
      <button type="button" className="btn btn-secondary min-h-11 px-4 text-[13px]" aria-label={describedBy} onClick={() => setStep("asking")}>
        {label}
      </button>
    );
  }
  return (
    <div role="group" aria-label={question} className="flex flex-col gap-2">
      <p className="text-[14px] leading-snug">{question}</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-primary min-h-11 px-4 text-[13px]" onClick={() => void confirm()} disabled={step === "busy"}>
          {step === "busy" ? busyLabel : confirmLabel}
        </button>
        <button ref={keep} type="button" className="btn btn-secondary min-h-11 px-4 text-[13px]" onClick={() => setStep("idle")} disabled={step === "busy"}>
          Keep it
        </button>
      </div>
      {failed ? (
        <p role="alert" className="text-[14px] text-danger">
          That didn't work. Try again.
        </p>
      ) : null}
    </div>
  );
}

/** The signed-in account's own apps. `onDisconnect` should reject if it fails. */
export function ConnectedAppsList({
  connections,
  onDisconnect,
  label,
  children,
}: {
  connections: Connection[];
  onDisconnect: (c: Connection) => Promise<void>;
  label: string;
  children?: ReactNode;
}) {
  return (
    <>
      <ul className="mt-3 flex flex-col gap-3" aria-label={label}>
        {connections.map((c) => (
          <li key={c.id} className="rounded-[16px] border border-line bg-panel p-4" data-testid="connected-app">
            <p className="break-words text-[17px] font-semibold leading-snug">{c.client.name}</p>
            <p className="mt-0.5 text-[14px]">
              <AppHost host={c.client.host} verified={c.client.verified} />
            </p>
            <p className="mt-3 text-[14px] leading-snug">
              <span className="text-muted">Can see: </span>
              {scopeWords(c.scopes).join(", ") || "Nothing yet"}
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-line pt-3">
              <p className="text-[13px] text-muted">
                <span className="block">Connected {shortDate(c.createdAt)}</span>
                <span className="block">{c.lastUsedAt ? `Last used ${shortDate(c.lastUsedAt)}` : "Not used yet"}</span>
              </p>
              <InlineConfirm
                label="Disconnect"
                describedBy={`Disconnect ${c.client.name}`}
                question={`Disconnect ${c.client.name}? It stops working at once and has to ask you again.`}
                confirmLabel="Yes, disconnect"
                busyLabel="Disconnecting…"
                onConfirm={() => onDisconnect(c)}
              />
            </div>
          </li>
        ))}
      </ul>
      {children}
    </>
  );
}
